import React, { useState, useEffect } from 'react';
import { JSONPathTesterService } from '../../lib/services/jsonpathTesterService';
import { EncodingOperation } from '../../lib/types/EncodingOperation';
import './JSONPathTester.css';

export const JSONPathTester: React.FC = () => {
  const [jsonInput, setJsonInput] = useState<string>(JSONPathTesterService.getSampleJSON());
  const [jsonpathQuery, setJsonpathQuery] = useState<string>('$.store.book[*].author');
  const [result, setResult] = useState<EncodingOperation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCheatSheet, setShowCheatSheet] = useState(false);

  // Process JSONPath query with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (jsonInput && jsonpathQuery) {
        processQuery();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [jsonInput, jsonpathQuery]);

  const processQuery = async () => {
    setIsProcessing(true);
    try {
      const operation = await JSONPathTesterService.evaluate(jsonInput, jsonpathQuery);
      setResult(operation);
    } catch (error) {
      console.error('JSONPath processing error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExampleClick = (query: string) => {
    setJsonpathQuery(query);
  };

  const handleClear = () => {
    setJsonInput('');
    setJsonpathQuery('');
    setResult(null);
  };

  const handleLoadSample = () => {
    setJsonInput(JSONPathTesterService.getSampleJSON());
    setJsonpathQuery('$.store.book[*].author');
  };

  const handleCopyResult = async () => {
    if (result?.outputData && typeof result.outputData === 'string') {
      try {
        await navigator.clipboard.writeText(result.outputData);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const examples = JSONPathTesterService.getExampleQueries();

  return (
    <div className="jsonpath-tester">
      <h1>JSONPath Tester</h1>
      <p className="description">
        Test JSONPath expressions against JSON data and see results in real-time.
      </p>

      <div className="jsonpath-container">
        <div className="input-section">
          <div className="input-group">
            <label htmlFor="json-input">
              JSON Data
              <button
                className="btn-secondary btn-small"
                onClick={handleLoadSample}
                type="button"
                aria-label="Load sample JSON"
              >
                Load Sample
              </button>
            </label>
            <textarea
              id="json-input"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="Enter JSON data..."
              rows={12}
              spellCheck={false}
              aria-label="JSON input data"
            />
          </div>

          <div className="input-group">
            <label htmlFor="jsonpath-query">
              JSONPath Query
              <button
                className="btn-secondary btn-small"
                onClick={() => setShowCheatSheet(!showCheatSheet)}
                type="button"
                aria-label="Toggle cheat sheet"
                aria-expanded={showCheatSheet}
              >
                {showCheatSheet ? 'Hide' : 'Show'} Cheat Sheet
              </button>
            </label>
            <input
              type="text"
              id="jsonpath-query"
              value={jsonpathQuery}
              onChange={(e) => setJsonpathQuery(e.target.value)}
              placeholder="Enter JSONPath query (e.g., $.store.book[*].author)"
              spellCheck={false}
              aria-label="JSONPath query"
            />
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={handleClear}
              type="button"
              aria-label="Clear all inputs"
            >
              Clear
            </button>
          </div>
        </div>

        {showCheatSheet && (
          <div className="cheat-sheet" role="region" aria-label="JSONPath cheat sheet">
            <h3>JSONPath Cheat Sheet</h3>
            <div className="examples-grid">
              {examples.map((example) => (
                <div key={example.name} className="example-item">
                  <button
                    className="example-button"
                    onClick={() => handleExampleClick(example.query)}
                    type="button"
                    aria-label={`Use example: ${example.name}`}
                  >
                    <div className="example-name">{example.name}</div>
                    <code className="example-query">{example.query}</code>
                    <div className="example-description">{example.description}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="output-section">
          <div className="output-header">
            <label htmlFor="jsonpath-result">Result</label>
            <div className="output-actions">
              {isProcessing && (
                <span className="processing-badge" aria-live="polite">
                  Processing...
                </span>
              )}
              {result?.status === 'success' && (
                <span className="match-count" aria-live="polite">
                  {result.metadata?.resultCount || 0} result{result.metadata?.resultCount !== 1 ? 's' : ''}
                </span>
              )}
              <button
                className="btn-secondary btn-small"
                onClick={handleCopyResult}
                disabled={!result?.outputData}
                type="button"
                aria-label="Copy result to clipboard"
              >
                Copy
              </button>
            </div>
          </div>

          <textarea
            id="jsonpath-result"
            value={
              result?.status === 'error'
                ? result.errorMessage || 'An error occurred'
                : (result?.outputData as string) || ''
            }
            readOnly
            rows={12}
            placeholder="Results will appear here..."
            className={result?.status === 'error' ? 'error' : ''}
            spellCheck={false}
            aria-label="JSONPath query result"
            aria-live="polite"
          />

          {result?.metadata && (
            <div className="metadata" aria-label="Query metadata">
              <span>Query: <code>{result.metadata.query}</code></span>
              <span>Results: {result.metadata.resultCount}</span>
              <span>Input: {result.metadata.inputLength} chars</span>
              <span>Output: {result.metadata.outputLength} chars</span>
            </div>
          )}
        </div>
      </div>

      <noscript>
        <div className="noscript-warning">
          This tool requires JavaScript to be enabled for interactive JSONPath testing.
        </div>
      </noscript>
    </div>
  );
};
